import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';

export interface UrlPromptPopoverHandle {
  show: (event: React.SyntheticEvent, initialValue?: string) => void;
}

interface Props {
  id: string;
  label: string;
  placeholder: string;
  onSubmit: (url: string) => void;
}

export const UrlPromptPopover = forwardRef<UrlPromptPopoverHandle, Props>(({ id, label, placeholder, onSubmit }, ref) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const [url, setUrl] = useState('');

  useImperativeHandle(ref, () => ({
    show: (event, initialValue = '') => {
      setUrl(initialValue);
      overlayRef.current?.toggle(event);
    },
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The overlay is a portal, but React still bubbles its submit to the form of the page (a post would be saved, a promotion sent).
    e.stopPropagation();
    // The submit button is disabled while the URL is blank, and a form whose default button is disabled is not submitted
    // with Enter, so the URL is never blank here.
    onSubmit(url.trim());
    overlayRef.current?.hide();
    setUrl('');
  };

  return (
    <OverlayPanel ref={overlayRef} className='rich-text-url-popover' dismissable>
      <form onSubmit={handleSubmit}>
        <label htmlFor={id}>{label}</label>
        <div className='rich-text-url-popover-input'>
          <InputText
            id={id}
            name={id}
            autoFocus
            value={url}
            placeholder={placeholder}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button type='submit' icon={PrimeIcons.CHECK} disabled={!url.trim()} />
        </div>
      </form>
    </OverlayPanel>
  );
});

UrlPromptPopover.displayName = 'UrlPromptPopover';
